import { useEffect, useState } from 'react'

import { reportAssetUrl } from '../lib/api'

// The report document itself — the Crystal layout, reproduced in the browser.
//
// Behavioural specification: the `.rpt` templates, converted to full-fidelity
// JSON at `oldcode/report/json/`. The legacy loads the template into a Crystal
// viewer (`EchocardiographyReport.aspx.vb:217`, and the equivalent branch in
// every other worksheet), refreshes it against
// `tblStudyReport_GetStudyReportById`, and exports a PDF.
//
// Crystal positions every object absolutely, in twips, on a US Letter page. The
// JSON carries both twips and pixels, so the faithful reproduction is an
// absolutely-positioned layer per section at the pixel geometry the template
// gives — not a re-flow into CSS grid, which would move things.
//
// This is one component so the report hub and the standalone view cannot drift
// apart, and so that what either shows is what `document_html` prints: both draw
// the same model with the same geometry, the same fonts and the same 1.0 line
// spacing.
//
// Read-only. The legacy regenerates and *saves* on view; that is not reproduced.

interface Font {
  family: string
  size_pt: number
  bold: boolean
  italic: boolean
  underline: boolean
  strikethrough: boolean
}
// The contract is `GET /api/v1/studies/{id}/report/render`. `kind` is
// lowercase ('text' | 'field' | 'line' | 'box' | 'picture'); geometry is in
// CSS pixels already converted from Crystal's twips.
interface Element {
  kind: string
  name?: string
  left: number
  top: number
  width: number
  height: number
  font?: Font | null
  text?: string | null
  value?: string | null
  binding?: string | null
  column?: string | null
  formula?: string | null
  template_text?: string | null
  suppress?: boolean
  multiline?: boolean
  // Picture objects only. `image` is a URL under /api/v1/reports/assets/;
  // `placeholder` means the mapping had nothing for this object.
  image?: string | null
  image_format?: string | null
  placeholder?: boolean
  note?: string | null
}
interface Section {
  name: string
  kind: string
  height_px: number
  top_px: number
  suppress?: boolean
  elements: Element[]
}
interface Diagnostics {
  elements: number
  unresolved_fields: string[]
  unresolved_field_count: number
  formulas_not_evaluated: string[]
  formulas_not_evaluated_count: number
  warnings: string[]
  // True when the template binds findings fields and every one of them is empty
  // for this study — a well-formed report with no clinical content in it.
  no_clinical_content?: boolean
  clinical_fields_bound?: number
  clinical_fields_populated?: number
}
interface Page {
  index: number
  number: number
  height_px: number
  sections: Section[]
}
/** Why this template was chosen — the transcribed `Select Case`, as data.
 *
 * Declared as `string` until a screenshot showed the toolbar reading
 * `Template SMCEchoReport · [object Object]`. It has always been an object;
 * nothing type-checked it because the API response is untyped at the boundary.
 */
export interface TemplateResolution {
  matched_on: string
  source?: string | null
  note?: string | null
  requested?: string | null
  substituted?: string | null
}

interface RenderModel {
  template: string
  study_type?: string
  reading_facility?: string | null
  study_facility?: string | null
  template_resolution?: TemplateResolution
  width_px: number
  height_px: number
  sections: Section[]
  // The stack flowed onto sheets, page header and footer repeated on each.
  // `sections` is still the flat stack; draw from `pages`.
  pages?: Page[]
  page_count?: number
  diagnostics?: Diagnostics
}

export type { Element, Font, Page, RenderModel, Section }

export default function ReportDocument({
  model,
  showBindings = false,
}: {
  model: RenderModel
  showBindings?: boolean
}) {
  const pageWidth = model.width_px ?? 816 // US Letter at 96dpi
  const pageHeight = model.height_px ?? 1056

  // One sheet per page, as Crystal would print it. A model built before
  // pagination existed has no `pages`; the flat stack is one sheet. Both are
  // defaulted because a model arriving without either — a truncated response,
  // an error body that parsed — must not take the page down with it.
  const pages = model.pages ?? [
    { index: 0, number: 1, height_px: pageHeight, sections: model.sections ?? [] },
  ]

  return (
    <>
      {pages.map((page) => (
        <div
          key={page.index}
          className="report-page"
          style={{ width: pageWidth, height: page.height_px }}
          data-page={page.number}
        >
          {(page.sections ?? [])
            .filter((s) => !s.suppress && s.elements.length > 0)
            .map((s, si) => (
              <section
                key={`${page.index}-${s.name}-${si}`}
                className="report-section"
                style={{ top: s.top_px, height: s.height_px, width: pageWidth }}
                data-section={s.name}
              >
                {s.elements
                  .filter((el) => !el.suppress)
                  .map((el, i) => (
                    <ReportElement
                      key={`${s.name}-${i}`}
                      el={el}
                      showBindings={showBindings}
                    />
                  ))}
              </section>
            ))}
        </div>
      ))}
    </>
  )
}

// Liberation Sans and Liberation Serif have the same advance widths as Arial
// and Times New Roman, so substituting them does not move anything.
function fontStack(name: string) {
  const serif = /times|serif|roman|georgia|garamond/i.test(name)
  return serif
    ? `"${name}", "CRM Serif", "Liberation Serif", serif`
    : `"${name}", "CRM Sans", "Liberation Sans", sans-serif`
}

function ReportElement({ el, showBindings }: { el: Element; showBindings: boolean }) {
  const style: React.CSSProperties = {
    left: el.left,
    top: el.top,
    width: el.width,
    // Crystal's height is the design box; text that runs longer must not be
    // clipped, so the box is a minimum rather than a fixed height.
    minHeight: el.height,
    // Map the template's font onto the metric-compatible face bundled with the
    // app, so a box Crystal sized for Arial still fits. Falls back to the real
    // font if the viewer happens to have it.
    fontFamily: el.font?.family ? fontStack(el.font.family) : undefined,
    fontSize: el.font?.size_pt ? `${el.font.size_pt}pt` : undefined,
    fontWeight: el.font?.bold ? 700 : undefined,
    fontStyle: el.font?.italic ? 'italic' : undefined,
    textDecoration: el.font?.underline ? 'underline' : undefined,
  }

  if (el.kind === 'line' || el.kind === 'box') {
    return <div className="rp-line" style={{ ...style, height: Math.max(el.height, 1) }} />
  }

  if (el.kind === 'picture') {
    if (!el.image) {
      return (
        <div className="rp-picture" style={style} title={el.note ?? 'No image mapped'}>
          {showBindings && <span className="rp-tag">picture</span>}
        </div>
      )
    }
    // Crystal scales a picture to fill its box and does not preserve the
    // stored aspect ratio, so `fill` is the faithful choice: the UEA arterial
    // diagram is stored 493x502 and drawn 396x471, and that squash is the
    // report as it has always looked.
    return (
      <ReportPicture
        src={el.image}
        style={{ ...style, height: el.height }}
        title={showBindings ? `${el.name} — ${el.image_format}` : undefined}
      />
    )
  }

  // `text` is what the server resolved for both kinds: the literal for a text
  // object, the bound value for a field. `value` is the raw datum.
  const content = el.text ?? el.value
  return (
    <div
      className={`${el.kind === 'field' ? 'rp-field' : 'rp-text'}${
        el.multiline ? ' rp-grow' : ''
      }`}
      style={style}
      title={el.column ?? el.formula ?? undefined}
    >
      {content}
      {showBindings && (el.column || el.formula) && (
        <span className="rp-tag">{el.column ?? el.formula}</span>
      )}
    </div>
  )
}

// One picture, fetched with the caller's credentials.
//
// An `<img src>` pointing at the API would be an unauthenticated request; see
// `reportAssetUrl`. Until the blob arrives the box is held open at its final
// size so nothing around it moves when the image lands.
function ReportPicture({
  src,
  style,
  title,
}: {
  src: string
  style: React.CSSProperties
  title?: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    reportAssetUrl(src)
      .then((u) => {
        if (!cancelled) setUrl(u)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [src])

  if (!url) {
    return (
      <div
        className="rp-picture"
        style={style}
        title={failed ? `Could not load ${src}` : title}
      />
    )
  }
  return <img className="rp-picture-img" style={style} src={url} alt="" title={title} />
}
